import { Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';

@Component({
  selector: 'app-confirm-dialog',
  templateUrl: './confirm-dialog.component.html',
  styleUrls: ['./confirm-dialog.component.css'],
})
export class ConfirmDialogComponent implements OnChanges {
  @Input() visible = false;
  @Input() heading = 'Confirmation';
  @Input() message = '';
  @Input() slideText = 'Slide to confirm';
  @Input() doneText = 'Confirmed';
  @Input() cancelText = 'Cancel';
  @Input() iconClass = 'pi pi-exclamation-triangle';
  @Input() slideIconClass = 'pi pi-angle-double-right';
  @Input() cancelIconClass = 'pi pi-times';
  @Input() confirmText = 'Yes';
  @Input() confirmIconClass = 'pi pi-check';
  @Input() variant: 'primary' | 'secondary' = 'primary';
  @Output() confirmed = new EventEmitter<void>();
  @Output() cancelled = new EventEmitter<void>();

  @ViewChild('track') private track?: ElementRef<HTMLDivElement>;

  progress = 0;
  handleLeft = 3;
  dragging = false;
  completed = false;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible']?.currentValue) this.resetSlider();
  }

  startDrag(event: PointerEvent): void {
    if (this.completed) return;
    event.preventDefault();
    this.dragging = true;
    (event.currentTarget as HTMLButtonElement).setPointerCapture(event.pointerId);
  }

  moveDrag(event: PointerEvent): void {
    if (!this.dragging || !this.track) return;

    const trackElement = this.track.nativeElement;
    const handle = event.currentTarget as HTMLButtonElement;
    const handleWidth = handle.offsetWidth;
    const maxLeft = trackElement.clientWidth - handleWidth - 3;
    const left = Math.min(maxLeft, Math.max(3, event.clientX - trackElement.getBoundingClientRect().left - handleWidth / 2));
    this.setProgress(maxLeft > 3 ? (left - 3) / (maxLeft - 3) : 0);
  }

  endDrag(event: PointerEvent): void {
    if (!this.dragging) return;
    this.moveDrag(event);
    this.dragging = false;

    if (this.progress >= 0.95) {
      this.complete();
    } else {
      this.resetSlider();
    }
  }

  onSliderKeydown(event: KeyboardEvent): void {
    if (this.completed) return;

    const nextProgress = {
      ArrowRight: this.progress + 0.1,
      ArrowUp: this.progress + 0.1,
      ArrowLeft: this.progress - 0.1,
      ArrowDown: this.progress - 0.1,
      Home: 0,
      End: 1,
    }[event.key];

    if (nextProgress !== undefined) {
      event.preventDefault();
      this.setProgress(nextProgress);
    }

    if (this.progress >= 0.95) this.complete();
  }

  cancel(): void {
    if (!this.completed) this.cancelled.emit();
  }

  confirm(): void {
    if (this.variant === 'secondary') this.confirmed.emit();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.visible) this.cancel();
  }

  private setProgress(progress: number): void {
    this.progress = Math.min(1, Math.max(0, progress));
    if (this.track) this.handleLeft = 3 + Math.max(0, this.track.nativeElement.clientWidth - 44) * this.progress;
  }

  private resetSlider(): void {
    this.progress = 0;
    this.handleLeft = 3;
    this.dragging = false;
    this.completed = false;
  }

  private complete(): void {
    if (this.completed) return;
    this.completed = true;
    this.setProgress(1);
    setTimeout(() => this.confirmed.emit(), 250);
  }
}